import { Fragment, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import { ParticleOrb } from "./ParticleOrb";
import { PatientBrief } from "./PatientBrief";
import { GlassComposer } from "./GlassComposer";
import { AiFlow } from "./AiFlow";
import { UserPlus, Pill, Send, CalendarPlus, FileBadge, X, Loader2 } from "lucide-react";

const TILES = [
  { id: "patient", label: "Nuevo paciente", icon: UserPlus },
  { id: "prescription", label: "Nueva receta", icon: Pill },
  { id: "referral", label: "Nuevo referido", icon: Send },
  { id: "appointment", label: "Nueva cita", icon: CalendarPlus },
  { id: "certificate", label: "Nuevo certificado", icon: FileBadge },
];

const inline = (s) =>
  s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : <Fragment key={i}>{part}</Fragment>
  );

const Markdown = ({ text }) => (
  <div className="space-y-1">
    {text.split("\n").map((line, i) => {
      const t = line.trim();
      if (!t) return <div key={i} className="h-1" />;
      if (/^#{1,4}\s/.test(t)) return <div key={i} className="font-semibold mt-1">{inline(t.replace(/^#+\s/, ""))}</div>;
      if (/^[-*•]\s/.test(t)) return <div key={i} className="flex gap-1.5"><span>•</span><span>{inline(t.slice(2))}</span></div>;
      return <div key={i}>{inline(t)}</div>;
    })}
  </div>
);

const PatientPicker = ({ onPick, onClose }) => {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/ai/patients", { params: { q } }).then(({ data }) => setList(data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="cai-glass rounded-[20px] p-2 mb-2 cai-fade-in" data-testid="cai-patient-picker">
      <div className="flex items-center gap-2 px-2">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Adjuntar paciente…"
          data-testid="cai-picker-input"
          className="cai-input flex-1 h-9 text-[13px] text-white placeholder:text-white/70"
        />
        <button type="button" onClick={onClose} className="text-white/80 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="max-h-40 overflow-y-auto cai-scroll">
        {list.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick(p)}
            data-testid={`cai-picker-${p.id}`}
            className="w-full text-left px-3 h-9 rounded-xl text-[13px] text-white hover:bg-white/15 transition-colors truncate"
          >
            {p.full_name}
          </button>
        ))}
      </div>
    </div>
  );
};

export const AiTab = ({ routePatientId, resetKey, openSession }) => {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attached, setAttached] = useState(null);
  const [picking, setPicking] = useState(false);
  const [flow, setFlow] = useState(null);
  const [hint, setHint] = useState("");
  const [routePatient, setRoutePatient] = useState(null);
  const flowRef = useRef(null);
  const endRef = useRef(null);
  const patientId = routePatientId || attached?.id || null;

  useEffect(() => {
    setMessages([]);
    setSessionId(null);
    setFlow(null);
    setAttached(null);
    setPicking(false);
    setRoutePatient(null);
    if (routePatientId) api.get(`/patients/${routePatientId}`).then(({ data }) => setRoutePatient(data.patient)).catch(() => {});
  }, [routePatientId, resetKey]);

  useEffect(() => {
    if (!openSession) return;
    setFlow(null);
    setPicking(false);
    setAttached(
      openSession.patient_id && openSession.patient_id !== routePatientId
        ? { id: openSession.patient_id, full_name: openSession.patient_name || "Paciente" }
        : null
    );
    api
      .get(`/ai/sessions/${openSession.session_id}`)
      .then(({ data }) => {
        setMessages(data.map((m) => ({ role: m.role, content: m.content })));
        setSessionId(openSession.session_id);
      })
      .catch(() => toast.error("No pude abrir la conversación"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSession]);

  const startFlow = (type, patient) =>
    setFlow({
      type,
      patient: type === "patient" ? null : patient || routePatient || (routePatientId ? { id: routePatientId, full_name: "este paciente" } : null),
      key: Date.now(),
    });
  const exitFlow = (text) => {
    setFlow(null);
    if (text) ask(text);
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  const ask = async (question) => {
    setMessages((m) => [...m, { role: "user", content: question }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { question, patient_id: patientId, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "No pude consultar la IA");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const empty = messages.length === 0 && !flow;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden cai-scroll px-5 pb-3">
        {empty ? (
          <>
            <ParticleOrb size={routePatientId ? 150 : 230} busy={loading} />
            <div className="text-center text-[16px] text-white mb-4 -mt-1" data-testid="cai-greeting">
              ¿En qué puedo ayudarte hoy?
            </div>
            {routePatientId && <div className="mb-2.5"><PatientBrief patientId={routePatientId} /></div>}
            {routePatientId && (
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 mb-2 mt-1">Acciones con IA</div>
            )}
            <div className="grid grid-cols-2 gap-2" data-testid="cai-tiles">
              {TILES.map(({ id, label, icon: Icon }, i) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => startFlow(id)}
                  data-testid={`cai-tile-${id}`}
                  className={`cai-glass cai-glass-hover rounded-[18px] p-3.5 text-left text-white flex ${
                    i === TILES.length - 1 && TILES.length % 2 ? "col-span-2 items-center gap-3 min-h-[56px]" : "flex-col justify-between min-h-[78px]"
                  }`}
                >
                  <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.6} />
                  <span className={`text-[12.5px] leading-tight ${i === TILES.length - 1 && TILES.length % 2 ? "" : "mt-2"}`}>{label}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="space-y-3 pt-1" data-testid="cai-messages">
            <ParticleOrb size={90} busy={loading} />
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} cai-fade-in`}>
                <div
                  data-testid={`cai-msg-${m.role}-${i}`}
                  className={`max-w-[88%] rounded-[20px] px-3.5 py-2.5 text-[12.5px] leading-relaxed ${
                    m.role === "user"
                      ? "bg-white/90 text-[#2B4A6F] rounded-br-md"
                      : "cai-glass text-white rounded-bl-md"
                  }`}
                >
                  {m.role === "user" ? m.content : <Markdown text={m.content} />}
                </div>
              </div>
            ))}
            {flow && (
              <AiFlow
                key={flow.key}
                ref={flowRef}
                type={flow.type}
                initialPatient={flow.patient}
                onHint={setHint}
                onExit={exitFlow}
                onRestart={startFlow}
              />
            )}
            {loading && (
              <div className="flex items-center gap-2 text-white/85 text-[12px]" data-testid="cai-thinking">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analizando…
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}
      </div>
      <div className="px-5 pb-4 pt-1">
        {picking && (
          <PatientPicker
            onPick={(p) => { setAttached(p); setPicking(false); setMessages([]); setSessionId(null); }}
            onClose={() => setPicking(false)}
          />
        )}
        {attached && !routePatientId && (
          <div className="mb-2 inline-flex items-center gap-1.5 cai-glass rounded-full pl-3 pr-1.5 h-7 text-[11.5px] text-white" data-testid="cai-attached">
            {attached.full_name}
            <button type="button" onClick={() => setAttached(null)} className="w-5 h-5 grid place-items-center rounded-full hover:bg-white/20">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        <GlassComposer
          testid="cai-composer"
          placeholder={flow ? hint : "Pregunta lo que sea…"}
          onSend={(t) => (flow ? flowRef.current?.text(t) : ask(t))}
          disabled={loading}
          onAttach={flow ? () => flowRef.current?.attach() : routePatientId ? undefined : () => setPicking((v) => !v)}
          attachTitle="Adjuntar paciente como contexto"
        />
        <div className="text-center text-[10.5px] text-white/80 mt-2.5">
          ConsuDoctor AI puede cometer errores. Verifica la información importante.
        </div>
      </div>
    </div>
  );
};
